import { AppRoleAttributeType } from "@/api-schema-enums";
import { useAttributeCreateMutation } from "@/common/api/attribute/use-attribute-create.query";
import { useAttributeSearchQuery } from "@/common/api/attribute/use-attribute-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { z } from "zod";
import { AttributeFormFields } from "./_components/attribute-form-fields";

const formSchema = z.object({
  applicationUuid: z.string(),
  attributeType: z.enum(AppRoleAttributeType),
});

type FormSchemaType = z.infer<typeof formSchema>;

export function AttributeView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ applicationUuid: string }>();
  const pageTitle = t("ram:AddAppRoleAttribute");
  const pageSubtitle = t("ram:SelectTheAttributeYouWantToAdd");
  const { data, isPending: attributeSearchIsPending } = useAttributeSearchQuery({
    options: {
      body: { application: [params.applicationUuid] },
    },
  });
  const { mutateAsync: attributeCreateMutateAsync } = useAttributeCreateMutation();

  const form = useForm<FormSchemaType>({
    defaultValues: {
      applicationUuid: params.applicationUuid,
      attributeType: undefined,
    },
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (data?.data) {
      form.reset({
        applicationUuid: params.applicationUuid,
        attributeType: undefined,
      });
    }
  }, [data, form, params.applicationUuid]);

  const onSubmit = async (values: FormSchemaType) => {
    await toastMutationStatus({
      mutationFn: () =>
        attributeCreateMutateAsync({
          applicationUuid: values.applicationUuid,
          type: values.attributeType,
        }),
      successMessage: t("ram:TheAttributeRightsHaveBeenAdded"),
      errorMessage: t("ram:ErrorAddingAttributeRights"),
    });

    navigate("/");
  };

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
      {attributeSearchIsPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader size={LoaderSize.Large} />
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardContent className="space-y-6">
                <AttributeFormFields form={form} data={data?.data ?? []} />
              </CardContent>
            </Card>

            <div className="flex flex-wrap justify-end gap-4">
              <Button
                type="button"
                variant={ButtonVariant.Outlined}
                colorVariant={ButtonColorVariant.Secondary}
                onClick={() => navigate("/")}
              >
                {t("common:Cancel")}
              </Button>
              <Button type="submit" disabled={!form.formState.isValid}>
                {t("ram:AddPermissions")}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </CenteredLayout>
  );
}
